import { Field, FieldProps, useFormikContext } from 'formik';
import { ReactionPreferenceOptionsForUser } from 'lib/const';
import { PackageName } from 'lib/const/PackageAdditionalInfo';
import * as React from 'react';
import { MdModeEdit } from 'react-icons/md';
import styled from 'styled-components/macro';
import { theme } from '../../../../../../lib/style';
import { NotificationSoundComponent } from './Notifications/NotificationSoundComponent';
import { NotificationPreferencesComponent } from './Notifications/NotificationPreferences';
import { TimezoneSelector } from 'lib/components/timezoneSelector/TimezoneSelector';
import { UserValues } from './userValidator';
import { useTranslation } from 'react-i18next';
import { FormikDropdownField } from 'lib/components/formik/FormikDropdown';
import { Flex, FlexProps } from 'lib/components/styles/layout';
import { FormikRadioField } from 'lib/components/formik/FormikRadioField';

interface FormProps {
  setSearch?: (text: string) => void;
  deptList?: any;
  setDeptValue?: (id: any) => void;
  deptLoading?: any;
  showReactionsPreference?: any;
  leadNotificationPreferences?: Record<
    string,
    Record<string, Array<{ name: string; value: boolean }>>
  >;
  showLeadsNotifPref?: boolean;
  packageId?:
    | PackageName.LEGACY
    | PackageName.FREE
    | PackageName.PRO
    | PackageName.TEAMS
    | PackageName.ENTERPRISE;
}

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: flex-start;
  width: 100%;
  padding: 0.5rem 0;
  @media screen and (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FieldLabel = styled.label`
  width: 120px;
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: 8px;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.palette.gray60};
`;

const SectionTitle = styled.p`
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  margin: 0;
  width: 100%;
  max-width: 200px;
  line-height: 24px;
`;

const IconWrapper = styled.div`
  position: absolute;
  color: #9297a2;
  margin-top: 8px;
  margin-left: 135px;
`;

const RowWrapper = styled.div`
  width: 100%;
  div:first-child,
  p:first-child {
    padding-top: 0px;
  }
`;

const FieldContainer = styled.section`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const TextArea = styled.textarea`
  display: block;
  width: 100%;
  max-width: 800px;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  background-color: '#fff';
  background-clip: padding-box;
  border: 1px solid #ced4da;
  color: #495057;
  border-radius: 0.25rem;
  box-sizing: border-box;
  resize: none;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  &:focus {
    color: #495057;
    background-color: #fff;
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`;

export const PreferencesForm = (props: FormProps) => {
  const {
    showReactionsPreference,
    packageId = 0,
    leadNotificationPreferences,
    showLeadsNotifPref,
  } = props;

  const { isSubmitting, setFieldValue, values } =
    useFormikContext<UserValues>();

  const { t } = useTranslation();
  const isFreemiumUser = packageId === PackageName.FREE;

  const extendStyles = {
    labelStyles: {
      minWidth: '125px',
      maxWidth: '125px',
      maxHeight: '40px',
    },
    mainWrapper: { flexDirection: 'row' },
  } as {
    labelStyles: React.CSSProperties;
    mainWrapper: FlexProps;
  };

  return (
    <>
      {/* System Settings */}
      <div
        style={{
          display: 'flex',
          maxWidth: 700,
        }}
      >
        <SectionTitle>{t('System Settings')}</SectionTitle>
        <RowWrapper>
          {/* Timezone */}
          <Row>
            <FieldLabel>{t('Timezone')}</FieldLabel>
            <Field type='select' name='timezone'>
              {({ field }: FieldProps) => {
                const { value } = field;
                return (
                  <TimezoneSelector
                    value={value}
                    onChange={newValue =>
                      setFieldValue('timezone', newValue?.utc[0])
                    }
                    disabled={isSubmitting}
                    useUtcValue={true}
                  />
                );
              }}
            </Field>
          </Row>
        </RowWrapper>
      </div>

      <div
        style={{
          display: 'flex',
          maxWidth: 700,
          marginTop: 8,
        }}
      >
        {!isFreemiumUser && (
          <>
            <SectionTitle>{t('Videos')}</SectionTitle>
            <RowWrapper>
              <Row>
                <FormikDropdownField
                  name={'playButtonPosition'}
                  label={t('Play Button Position')}
                  options={[
                    { value: 0, label: t('Center') },
                    { value: 1, label: t('Top Left') },
                    { value: 2, label: t('Top Right') },
                    { value: 3, label: t('Bottom Left') },
                    { value: 4, label: t('Bottom Right') },
                  ]}
                  disabled={isSubmitting}
                  extendStyles={extendStyles}
                />
              </Row>
              {showReactionsPreference && (
                <Row>
                  <FieldLabel>{t('Reactions & Comments')}</FieldLabel>
                  <Flex flexDirection='column' gap='8px'>
                    <FormikRadioField
                      label={t('Turn on by default')}
                      name='reactionsPreference'
                      value={ReactionPreferenceOptionsForUser.TURN_ON_DEFAULT}
                      disabled={isSubmitting}
                    />
                    <FormikRadioField
                      label={t('Turn off by default')}
                      name='reactionsPreference'
                      value={ReactionPreferenceOptionsForUser.TURN_OFF_DEFAULT}
                      disabled={isSubmitting}
                    />
                  </Flex>
                </Row>
              )}
            </RowWrapper>
          </>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          maxWidth: 700,
          marginTop: 8,
        }}
      >
        <SectionTitle>{t('Video Sharing')}</SectionTitle>
        <RowWrapper>
          <Row>
            <FieldLabel>{t('Link Text')}</FieldLabel>
            <Field name='videoShareLinkText'>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextArea
                      {...field}
                      disabled={isSubmitting}
                      placeholder={t('Link Text')}
                    />
                  </FieldContainer>
                );
              }}
            </Field>
            <IconWrapper style={{ marginTop: 20, marginLeft: 475 }}>
              <MdModeEdit width={30} height={30} />
            </IconWrapper>
          </Row>
        </RowWrapper>
      </div>
      <NotificationSoundComponent />
      <NotificationPreferencesComponent
        setFieldValue={setFieldValue}
        leadNotificationPreferences={leadNotificationPreferences}
        values={values}
        showLeadsNotifPref={showLeadsNotifPref}
      />
    </>
  );
};
